import React from 'react';
import { Row, Col, Card, Statistic, Table, Tag, Typography } from 'antd';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
    PieChart, Pie, Cell
} from 'recharts';
import {
    ThunderboltOutlined,
    BankOutlined,
    DatabaseOutlined,
    CloudOutlined
} from '@ant-design/icons';

const { Title } = Typography;

const Dashboard: React.FC = () => {
    // 模拟数据
    const powerData = [
        { time: '00:00', actual: 85, predicted: 82 },
        { time: '04:00', actual: 78, predicted: 80 },
        { time: '08:00', actual: 120, predicted: 115 },
        { time: '12:00', actual: 150, predicted: 148 },
        { time: '16:00', actual: 135, predicted: 140 },
        { time: '20:00', actual: 110, predicted: 112 },
    ];

    const stationData = [
        { name: '风电场A', value: 35, color: '#8884d8' },
        { name: '风电场B', value: 28, color: '#82ca9d' },
        { name: '光伏场C', value: 22, color: '#ffc658' },
        { name: '光伏场D', value: 15, color: '#ff7300' },
    ];

    const modelColumns = [
        {
            title: '模型名称',
            dataIndex: 'model',
            key: 'model',
        },
        {
            title: '预测精度',
            dataIndex: 'accuracy',
            key: 'accuracy',
            render: (accuracy: number) => (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={{
                        width: '100px',
                        height: '8px',
                        background: '#f0f0f0',
                        borderRadius: '4px',
                        marginRight: '8px'
                    }}>
                        <div style={{
                            width: `${accuracy}%`,
                            height: '100%',
                            background: '#1890ff',
                            borderRadius: '4px'
                        }} />
                    </div>
                    {accuracy}%
                </div>
            ),
        },
        {
            title: '状态',
            dataIndex: 'status',
            key: 'status',
            render: (status: string) => {
                let color = 'default';
                switch (status) {
                    case '运行中': color = 'green'; break;
                    case '训练中': color = 'blue'; break;
                    case '已停止': color = 'red'; break;
                }
                return <Tag color={color}>{status}</Tag>;
            },
        },
    ];

    const modelAccuracy = [
        { key: '1', model: '风电场A-1号机组预测模型', accuracy: 95.2, status: '运行中' },
        { key: '2', model: '光伏场B-1号方阵预测模型', accuracy: 93.8, status: '运行中' },
        { key: '3', model: '水电站C-1号机组预测模型', accuracy: 89.5, status: '已停止' },
        { key: '4', model: '风电场A-2号机组预测模型', accuracy: 91.2, status: '训练中' },
    ];

    return (
        <div className="page-transition">
            <Title level={2} style={{ marginBottom: '24px' }}>
                仪表板概览
            </Title>

            {/* 统计卡片 */}
            <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                <Col span={6}>
                    <Card>
                        <Statistic
                            title="当前总功率 (MW)"
                            value={1285.6}
                            prefix={<ThunderboltOutlined style={{ color: '#3f8600' }} />}
                            precision={1}
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card>
                        <Statistic
                            title="在线电厂数量"
                            value={8}
                            prefix={<BankOutlined style={{ color: '#1890ff' }} />}
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card>
                        <Statistic
                            title="数据更新率"
                            value={98.5}
                            precision={1}
                            suffix="%"
                            prefix={<DatabaseOutlined style={{ color: '#cf1322' }} />}
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card>
                        <Statistic
                            title="预测准确率"
                            value={94.2}
                            precision={1}
                            suffix="%"
                            prefix={<CloudOutlined style={{ color: '#722ed1' }} />}
                        />
                    </Card>
                </Col>
            </Row>

            {/* 图表区域 */}
            <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                <Col span={16}>
                    <Card
                        title="功率预测对比"
                        extra={<Tag color="blue">实时数据</Tag>}
                    >
                        <ResponsiveContainer width="100%" height={300}>
                            <LineChart data={powerData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="time" />
                                <YAxis />
                                <Tooltip />
                                <Legend />
                                <Line
                                    type="monotone"
                                    dataKey="actual"
                                    stroke="#8884d8"
                                    name="实际功率"
                                    strokeWidth={2}
                                />
                                <Line
                                    type="monotone"
                                    dataKey="predicted"
                                    stroke="#82ca9d"
                                    name="预测功率"
                                    strokeWidth={2}
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    </Card>
                </Col>
                <Col span={8}>
                    <Card title="场站功率分布">
                        <ResponsiveContainer width="100%" height={300}>
                            <PieChart>
                                <Pie
                                    data={stationData}
                                    cx="50%"
                                    cy="50%"
                                    labelLine={false}
                                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                    outerRadius={80}
                                    fill="#8884d8"
                                    dataKey="value"
                                >
                                    {stationData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                    ))}
                                </Pie>
                                <Tooltip />
                            </PieChart>
                        </ResponsiveContainer>
                    </Card>
                </Col>
            </Row>

            {/* 模型状态表格 */}
            <Card
                title="模型运行状态"
                extra={
                    <div>
                        <Tag color="green">2个运行中</Tag>
                        <Tag color="blue">1个训练中</Tag>
                        <Tag color="red">1个已停止</Tag>
                    </div>
                }
            >
                <Table
                    columns={modelColumns}
                    dataSource={modelAccuracy}
                    pagination={false}
                />
            </Card>
        </div>
    );
};

export default Dashboard; 